/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { FormEvent, useState } from "react";
import { Button } from "semantic-ui-react";
import styles from "./PaymentMethod.module.css";
import cStyles from "../Components.module.css";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
} from "@stripe/react-stripe-js";
import { Ico } from "../Icon/Icon";
import "./StripeElement.css";
import { PaymentMethodCreateParams } from "@stripe/stripe-js";
import powered from "./PoweredByStripe.svg";
import visa from "./visa.png";
import mastercard from "./mastercard.png";
import amex from "./amex.png";
import discover from "./discover.jpg";
import jcb from "./jcb.png";
import dci from "./dci.png";
import { useTranslation } from "react-i18next";
import { t } from "i18next";

interface PaymentMethodProps {
  onSubmitPaymentMethod: any;
  onCancel: any;
  actionTitle: string;
}

const cardHolderValidator =
  /^[a-zA-Z\xC0-\uFFFF]{1,20}(?: [a-zA-Z\xC0-\uFFFF]+){0,20}$/;

export const createPaymentMethod = async (
  stripe: any,
  elements: any,
  billingDetails: PaymentMethodCreateParams.BillingDetails
) => {
  if (!stripe || !elements) {
    return {
      message: t("pay_process_error"),
    };
  }

  const cardElement = elements.getElement(CardNumberElement);
  if (!cardElement) {
    return {
      message: t("process_error"),
    };
  }

  const { error, paymentMethod } = await stripe.createPaymentMethod({
    type: "card",
    card: cardElement,
    billing_details: billingDetails,
  });

  if (error || !paymentMethod) {
    const errorInfo = error ? error.message : "";
    return {
      message: `${t("failed_create_method")} ${errorInfo}`,
      error: error,
    };
  }

  return { paymentMethod: paymentMethod };
};

const PaymentMethod: React.FC<PaymentMethodProps> = ({
  onSubmitPaymentMethod,
  actionTitle,
}) => {
  const { t } = useTranslation(["billing", "common"]);

  const stripeElementOptions = {
    style: {
      base: {
        fontFamily: "Open Sans",
        fontSize: "16px",
        fontSmoothing: "antialiased",
        fontWeight: "400",
        color: "#353535",
        "::placeholder": {
          color: "#ddd",
        },
        ":focus": {
          color: "#353535",
        },
      },
      invalid: {
        color: "#ff8a8a",
      },
    },
  };

  const [nameOnCard, setNameOnCard] = useState<string>("");
  const [nameInvalid, setNameInvalid] = useState<boolean>(true);
  const [cardInvalid, setCardInvalid] = useState<boolean>(true);
  const [expirationInvalid, setExpirationInvalid] = useState<boolean>(true);
  const [cvcInvalid, setCvcInvalid] = useState<boolean>(true);

  const stripeElementCardOptions = {
    showIcon: true,
    ...stripeElementOptions,
  };

  const CardIcon = (props: any) => {
    return (
      <div className={styles.brandIcon}>
        <img src={props.icon} style={{ height: props.height }} alt="brand" />
      </div>
    );
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value: name } = event.target;
    setNameInvalid(!cardHolderValidator.test(name));
    setNameOnCard(name);
  };

  const checkElementEventInvalidState = (event: any) => {
    const { error, complete } = event;
    return error || !complete;
  };

  const isFormReady =
    !nameInvalid && !cardInvalid && !expirationInvalid && !cvcInvalid;

  const submitForm = (event: FormEvent) => {
    event.preventDefault();
    if (isFormReady) {
      onSubmitPaymentMethod({ name: nameOnCard });
    }
  };

  const localElementClass = (field: string, fieldInvalid: boolean) => {
    if (!field.length) {
      return styles.localElement;
    }

    if (!fieldInvalid) {
      return styles.localElementComplete;
    }

    return styles.localElementError;
  };

  return (
    <form onSubmit={submitForm} className={styles.form}>
      <input type="submit" hidden />

      <div className={styles.cardBrands}>
        <CardIcon icon={visa} height="50%" />
        <CardIcon icon={mastercard} height="100%" />
        <CardIcon icon={amex} height="90%" />
        <CardIcon icon={discover} height="95%" />
        <CardIcon icon={jcb} height="90%" />
        <CardIcon icon={dci} height="98%" />
      </div>

      <label htmlFor="nameOnCard" className={styles.label}>
        {t("name_on_card")}
      </label>
      <div className={localElementClass(nameOnCard, nameInvalid)}>
        <input
          id="nameOnCard"
          autoFocus
          maxLength={20}
          placeholder={t("name_on_card")}
          defaultValue={nameOnCard}
          onChange={handleNameChange}
        />
      </div>

      <label htmlFor="cardNumber" className={styles.label}>
        {t("card_number")}
      </label>
      <CardNumberElement
        onChange={(e) => {
          setCardInvalid(checkElementEventInvalidState(e));
        }}
        id="cardNumber"
        options={stripeElementCardOptions}
      />

      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            flex: "0.6",
          }}
        >
          <label htmlFor="expiry" className={styles.label}>
            {t("card_expiration")}
          </label>
          <CardExpiryElement
            onChange={(e) => {
              setExpirationInvalid(checkElementEventInvalidState(e));
            }}
            id="expiry"
            options={stripeElementOptions}
          />
        </div>

        <div style={{ display: "flex", flexDirection: "column", flex: "0.3" }}>
          <label htmlFor="cvc" className={styles.label}>
            CVC
          </label>
          <CardCvcElement
            onChange={(e) => {
              setCvcInvalid(checkElementEventInvalidState(e));
            }}
            id="cvc"
            options={stripeElementOptions}
          />
        </div>
      </div>

      <div className={styles.buttons}>
        <a
          href="https://www.stripe.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img src={powered} height="20" alt="Stripe" />
        </a>

        <Button
          type="submit"
          primary
          className={cStyles.buttonGreen}
          disabled={!isFormReady}
        >
          <Ico name="check" style={{ marginRight: "10px" }} />
          {actionTitle}
        </Button>
      </div>
    </form>
  );
};

export default PaymentMethod;
